import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, optionsService } from '../services/api';
import { sanitizePhone } from '../utils/phone';
import { formatPersonName } from '../utils/personName';
import { normalizeDni } from '../utils/dni';
import ConfirmPopup from '../components/ConfirmPopup';
import './CompleteProfile.css';

// Nombres amigables para mostrar en errores de validación del backend
const PROFILE_FIELD_LABELS = {
  first_name: 'Nombre/s',
  last_name: 'Apellido/s',
  dni: 'DNI',
  genero_id: 'Género',
  fecha_nacimiento: 'Fecha de nacimiento',
  telefono_celular: 'Teléfono celular',
  pais_id: 'País',
  pais_otro: 'País (Otro)',
  provincia_id: 'Provincia',
  provincia_otro: 'Provincia (Otro)',
  localidad_id: 'Partido',
  localidad_otro: 'Partido (Otro)',
  barrio_id: 'Barrio',
  barrio_otro: 'Barrio (Otro)',
  tipo_educacion_secundaria_id: 'Dónde estudiaste la Secundaria',
  nivel_estudio_id: 'Nivel de estudio',
  situacion_laboral_id: 'Situación laboral',
  area_trabajo_id: 'Área de trabajo',
  habilidades_ids: 'Habilidades',
  objetivo_curso_id: 'Objetivo del curso',
  horas_dedicacion_id: 'Horas de dedicación',
  tiene_computadora_internet: '¿Tiene computadora e internet?',
  fuente_informacion_id: '¿Cómo te enteraste?',
};

function formatProfileError(data) {
  if (!data || typeof data !== 'object') {
    return 'Error al actualizar el perfil. Por favor, intenta nuevamente.';
  }
  if (data.non_field_errors && Array.isArray(data.non_field_errors) && data.non_field_errors[0]) {
    return data.non_field_errors[0];
  }
  const entries = Object.entries(data).filter(([key]) => key !== 'non_field_errors');
  if (entries.length === 0) {
    return 'Error al actualizar el perfil. Por favor, intenta nuevamente.';
  }
  const fieldMessages = entries.map(([key, messages]) => {
    const label = PROFILE_FIELD_LABELS[key] || key;
    const msg = Array.isArray(messages) ? messages[0] : messages;
    return msg ? `${label}: ${msg}` : label;
  });
  if (fieldMessages.length === 1) {
    return `Revisá este campo — ${fieldMessages[0]}`;
  }
  return `Revisá estos campos — ${fieldMessages.join(' | ')}`;
}

const CompleteProfile = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(1);
  const [formData, setFormData] = useState({
    // Datos básicos
    first_name: '',
    last_name: '',
    // Datos personales
    dni: '',
    genero_id: '',
    fecha_nacimiento: '',
    telefono_celular: '',
    pais_id: '',
    pais_otro: '',
    provincia_id: '',
    provincia_otro: '',
    localidad_id: '',
    localidad_otro: '',
    barrio_id: '',
    barrio_otro: '',
    // Estudio y Trabajo
    tipo_educacion_secundaria_id: '',
    nivel_estudio_id: '',
    situacion_laboral_id: '',
    area_trabajo_id: '',
    habilidades_ids: [],
    // Información adicional
    objetivo_curso_id: '',
    horas_dedicacion_id: '',
    tiene_computadora_internet: '',
    fuente_informacion_id: '',
  });
  
  const [options, setOptions] = useState({
    generos: [],
    paises: [],
    provincias: [],
    localidades: [],
    barrios: [],
    tiposEducacionSecundaria: [],
    nivelesEstudio: [],
    situacionesLaborales: [],
    areasTrabajo: [],
    habilidades: [],
    objetivosCurso: [],
    horasDedicacion: [],
    fuentesInformacion: [],
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [selectedSituacionLaboral, setSelectedSituacionLaboral] = useState(null);
  const [completedFields, setCompletedFields] = useState({});
  // Habilidades "Otros": textos personalizados (una por línea), misma lógica que Registro y Perfil
  const [otrasHabilidades, setOtrasHabilidades] = useState(['']);

  // Cargar perfil existente y opciones al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar perfil existente
        const profile = await authService.getProfileExtended();
        
        // Obtener datos del usuario también
        const userData = await authService.getProfile();
        
        // Cargar opciones
        const [
          generos,
          paises,
          tiposEducacionSecundaria,
          nivelesEstudio,
          situacionesLaborales,
          areasTrabajo,
          habilidades,
          objetivosCurso,
          horasDedicacion,
          fuentesInformacion,
        ] = await Promise.all([
          optionsService.getGeneros(),
          optionsService.getPaises(),
          optionsService.getTiposEducacionSecundaria(),
          optionsService.getNivelesEstudio(),
          optionsService.getSituacionesLaborales(),
          optionsService.getAreasTrabajo(),
          optionsService.getHabilidades(),
          optionsService.getObjetivosCurso(),
          optionsService.getHorasDedicacion(),
          optionsService.getFuentesInformacion(),
        ]);

        // Determinar qué campos ya están completos
        const completed = {
          first_name: !!userData.first_name,
          last_name: !!userData.last_name,
          dni: !!profile.dni,
          genero_id: !!profile.genero?.id,
          fecha_nacimiento: !!profile.fecha_nacimiento,
          telefono_celular: !!profile.telefono_celular,
          pais_id: !!(profile.pais?.id || (profile.pais_otro || '').trim()),
          provincia_id: !!(profile.provincia?.id || (profile.provincia_otro || '').trim()),
          localidad_id: !!(profile.localidad?.id || (profile.localidad_otro || '').trim()),
          barrio_id: !!(profile.barrio?.id || (profile.barrio_otro || '').trim()),
          tipo_educacion_secundaria_id: !!profile.tipo_educacion_secundaria?.id,
          nivel_estudio_id: !!profile.nivel_estudio?.id,
          situacion_laboral_id: !!profile.situacion_laboral?.id,
          area_trabajo_id: !!profile.area_trabajo?.id,
          habilidades_ids: profile.habilidades && profile.habilidades.length > 0,
          objetivo_curso_id: !!profile.objetivo_curso?.id,
          horas_dedicacion_id: !!profile.horas_dedicacion?.id,
          tiene_computadora_internet: profile.tiene_computadora_internet !== null,
          fuente_informacion_id: !!profile.fuente_informacion?.id,
        };
        setCompletedFields(completed);

        // Actualizar formData con datos del perfil
        setFormData({
          first_name: formatPersonName(userData.first_name || ''),
          last_name: formatPersonName(userData.last_name || ''),
          dni: normalizeDni(profile.dni || ''),
          genero_id: profile.genero?.id || '',
          fecha_nacimiento: (profile.fecha_nacimiento || '').toString().substring(0, 10) || '',
          telefono_celular: sanitizePhone(profile.telefono_celular || ''),
          pais_id: profile.pais?.id ? String(profile.pais.id) : ((profile.pais_otro || '').trim() ? 'otro' : ''),
          pais_otro: profile.pais_otro || '',
          provincia_id: profile.provincia?.id ? String(profile.provincia.id) : ((profile.provincia_otro || '').trim() ? 'otro' : ''),
          provincia_otro: profile.provincia_otro || '',
          localidad_id: profile.localidad?.id ? String(profile.localidad.id) : ((profile.localidad_otro || '').trim() ? 'otro' : ''),
          localidad_otro: profile.localidad_otro || '',
          barrio_id: profile.barrio?.id ? String(profile.barrio.id) : ((profile.barrio_otro || '').trim() ? 'otro' : ''),
          barrio_otro: profile.barrio_otro || '',
          tipo_educacion_secundaria_id: profile.tipo_educacion_secundaria?.id || '',
          nivel_estudio_id: profile.nivel_estudio?.id || '',
          situacion_laboral_id: profile.situacion_laboral?.id || '',
          area_trabajo_id: profile.area_trabajo?.id || '',
          habilidades_ids: profile.habilidades?.map(h => h.id) || [],
          objetivo_curso_id: profile.objetivo_curso?.id || '',
          horas_dedicacion_id: profile.horas_dedicacion?.id || '',
          tiene_computadora_internet: profile.tiene_computadora_internet !== null ? String(profile.tiene_computadora_internet) : '',
          fuente_informacion_id: profile.fuente_informacion?.id || '',
        });
        const customNombres = (profile.habilidades || []).filter(
          h => !(habilidades || []).some(o => o.id === h.id)
        ).map(h => h.nombre);
        setOtrasHabilidades(customNombres.length ? customNombres : ['']);

        setOptions({
          generos,
          paises,
          provincias: [],
          localidades: [],
          barrios: [],
          tiposEducacionSecundaria,
          nivelesEstudio,
          situacionesLaborales,
          areasTrabajo,
          habilidades,
          objetivosCurso,
          horasDedicacion,
          fuentesInformacion,
        });

        // Cargar provincias/localidades/barrios si hay selección de catálogo
        if (profile.pais?.id) {
          const provincias = await optionsService.getProvincias(profile.pais.id);
          setOptions(prev => ({ ...prev, provincias }));
        }
        if (profile.provincia?.id) {
          const localidades = await optionsService.getLocalidades(profile.provincia.id);
          setOptions(prev => ({ ...prev, localidades }));
        }
        if (profile.localidad?.id) {
          const barrios = await optionsService.getBarrios(profile.localidad.id);
          setOptions(prev => ({ ...prev, barrios }));
        }

        setLoadingProfile(false);
        setLoadingOptions(false);
      } catch (err) {
        console.error('Error al cargar datos:', err);
        setLoadingProfile(false);
        setLoadingOptions(false);
      }
    };

    loadData();
  }, []);

  // Cargar provincias cuando se selecciona un país (no "Otro"). No limpiar formData al cargar opciones para no pisar valores del perfil.
  useEffect(() => {
    if (formData.pais_id && formData.pais_id !== 'otro') {
      optionsService.getProvincias(formData.pais_id)
        .then(provincias => {
          setOptions(prev => ({ ...prev, provincias, localidades: [], barrios: [] }));
        })
        .catch(() => setOptions(prev => ({ ...prev, provincias: [], localidades: [], barrios: [] })));
    } else if (formData.pais_id === 'otro') {
      setOptions(prev => ({ ...prev, provincias: [], localidades: [], barrios: [] }));
      setFormData(prev => ({ ...prev, provincia_id: '', localidad_id: '', barrio_id: '', barrio_otro: '' }));
    } else {
      setOptions(prev => ({ ...prev, provincias: [], localidades: [], barrios: [] }));
      setFormData(prev => ({ ...prev, provincia_id: '', provincia_otro: '', localidad_id: '', localidad_otro: '', barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.pais_id]);

  // Cargar partidos (localidades) cuando se selecciona una provincia (no "Otro"). No limpiar formData al cargar para no pisar partido/barrio del perfil.
  useEffect(() => {
    if (formData.provincia_id && formData.provincia_id !== 'otro') {
      optionsService.getLocalidades(formData.provincia_id)
        .then(localidades => {
          setOptions(prev => ({ ...prev, localidades, barrios: [] }));
        })
        .catch(() => setOptions(prev => ({ ...prev, localidades: [], barrios: [] })));
    } else if (formData.provincia_id === 'otro') {
      setOptions(prev => ({ ...prev, localidades: [], barrios: [] }));
      setFormData(prev => ({ ...prev, localidad_id: '', barrio_id: '', barrio_otro: '' }));
    } else {
      setOptions(prev => ({ ...prev, localidades: [], barrios: [] }));
      setFormData(prev => ({ ...prev, localidad_id: '', localidad_otro: '', barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.provincia_id]);

  // Cargar barrios cuando se selecciona un partido (no "Otro"). No limpiar formData al cargar para no pisar barrio del perfil.
  useEffect(() => {
    if (formData.localidad_id && formData.localidad_id !== 'otro') {
      optionsService.getBarrios(formData.localidad_id)
        .then(barrios => setOptions(prev => ({ ...prev, barrios })))
        .catch(() => setOptions(prev => ({ ...prev, barrios: [] })));
    } else {
      setOptions(prev => ({ ...prev, barrios: [] }));
      setFormData(prev => ({ ...prev, barrio_id: '', barrio_otro: '' }));
    }
  }, [formData.localidad_id]);

  // Actualizar situación laboral seleccionada
  useEffect(() => {
    if (formData.situacion_laboral_id) {
      const situacion = options.situacionesLaborales.find(
        s => s.id === parseInt(formData.situacion_laboral_id)
      );
      setSelectedSituacionLaboral(situacion);
      if (!situacion?.requiere_area) {
        setFormData(prev => ({ ...prev, area_trabajo_id: '' }));
      }
    } else {
      setSelectedSituacionLaboral(null);
    }
  }, [formData.situacion_laboral_id, options.situacionesLaborales]);

  const otrosId = options.habilidades?.find(h => (h.nombre || '').toLowerCase() === 'otros')?.id;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'habilidades_ids') {
        const habilidadId = parseInt(value);
        if (habilidadId === otrosId && !checked) {
          setOtrasHabilidades(['']);
        }
        setFormData(prev => ({
          ...prev,
          habilidades_ids: prev.habilidades_ids.includes(habilidadId)
            ? prev.habilidades_ids.filter(id => id !== habilidadId)
            : [...prev.habilidades_ids, habilidadId]
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      const finalValue = name === 'telefono_celular'
        ? sanitizePhone(value)
        : name === 'dni'
          ? normalizeDni(value)
          : value;
      setFormData(prev => {
        const next = { ...prev, [name]: finalValue };
        if (name === 'pais_id') {
          next.provincia_id = ''; next.provincia_otro = '';
          next.localidad_id = ''; next.localidad_otro = '';
          next.barrio_id = ''; next.barrio_otro = '';
        } else if (name === 'provincia_id') {
          next.localidad_id = ''; next.localidad_otro = '';
          next.barrio_id = ''; next.barrio_otro = '';
        } else if (name === 'localidad_id') {
          next.barrio_id = ''; next.barrio_otro = '';
        }
        return next;
      });
    }
    setError('');
  };

  const handleNext = () => {
    if (currentSection < 3) {
      setCurrentSection(currentSection + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Preparar datos: campos no completados se envían para completar; ubicación siempre se envía completa para evitar errores de validación en el backend
    const submitData = {};
    Object.keys(formData).forEach(key => {
      if (!completedFields[key]) {
        let value = formData[key];
        if (value === '') value = null;
        submitData[key] = value;
      }
    });

    // Siempre incluir ubicación completa (desde formData) para que el backend no reciba un PATCH parcial que deje campos nulos
    submitData.pais_id = formData.pais_id === 'otro' ? null : (formData.pais_id ? parseInt(formData.pais_id, 10) : null);
    submitData.pais_otro = (formData.pais_otro || '').trim();
    submitData.provincia_id = formData.provincia_id === 'otro' ? null : (formData.provincia_id ? parseInt(formData.provincia_id, 10) : null);
    submitData.provincia_otro = (formData.provincia_otro || '').trim();
    submitData.localidad_id = formData.localidad_id === 'otro' ? null : (formData.localidad_id ? parseInt(formData.localidad_id, 10) : null);
    submitData.localidad_otro = (formData.localidad_otro || '').trim();
    submitData.barrio_id = (formData.barrio_id && formData.barrio_id !== 'otro') ? parseInt(formData.barrio_id, 10) : null;
    submitData.barrio_otro = (formData.barrio_otro || '').trim();

    const idFields = [
      'genero_id', 'tipo_educacion_secundaria_id', 'nivel_estudio_id',
      'situacion_laboral_id', 'area_trabajo_id',
      'objetivo_curso_id', 'horas_dedicacion_id', 'fuente_informacion_id'
    ];
    idFields.forEach(field => {
      if (submitData[field] && !completedFields[field]) {
        submitData[field] = parseInt(submitData[field]);
      }
    });
    
    // Resolver "Otros" en habilidades: obtener o crear habilidades personalizadas (misma lógica que Registro y Perfil)
    if (submitData.habilidades_ids !== undefined && !completedFields.habilidades_ids) {
      const otrosIdNum = options.habilidades?.find(h => (h.nombre || '').toLowerCase() === 'otros')?.id;
      let habilidadesIdsFinal = (submitData.habilidades_ids || []).map(id => parseInt(id, 10));
      const tieneOtras = otrasHabilidades.some(t => t && t.trim());
      if (otrosIdNum && (habilidadesIdsFinal.includes(otrosIdNum) || tieneOtras)) {
        const textosOtras = otrasHabilidades.filter(t => t && t.trim());
        const customIds = [];
        for (const nombre of textosOtras) {
          try {
            const { id } = await optionsService.getOrCreateHabilidad(nombre);
            customIds.push(id);
          } catch (err) {
            setError(err.response?.data?.nombre?.[0] || 'Error al guardar una habilidad. Intentá de nuevo.');
            setLoading(false);
            return;
          }
        }
        habilidadesIdsFinal = habilidadesIdsFinal.filter(id => id !== otrosIdNum).concat(customIds);
      }
      submitData.habilidades_ids = habilidadesIdsFinal;
    }

    // Convertir tiene_computadora_internet a booleano
    if (submitData.tiene_computadora_internet !== undefined && !completedFields.tiene_computadora_internet) {
      if (submitData.tiene_computadora_internet !== null && submitData.tiene_computadora_internet !== '') {
        submitData.tiene_computadora_internet = submitData.tiene_computadora_internet === 'true' || submitData.tiene_computadora_internet === true;
      }
    }
    if (submitData.dni) submitData.dni = normalizeDni(submitData.dni);

    try {
      const response = await authService.updateProfile(submitData);
      
      // Actualizar usuario en localStorage
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        currentUser.profile_complete = response.profile_complete;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
      
      // Redirigir al dashboard si el perfil está completo
      if (response.profile_complete) {
        navigate('/dashboard');
      } else {
        setError('Por favor, completa todos los campos requeridos.');
      }
    } catch (err) {
      const data = err.response?.data;
      const errorMessage = formatProfileError(data);
      setError(errorMessage);
      setCurrentSection(1);
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions || loadingProfile) {
    return (
      <div className="complete-profile">
        <div className="complete-profile-container">
          <div className="complete-profile-loading">
            <div className="loading-spinner"></div>
            <p>Cargando formulario...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="complete-profile">
      <div className="complete-profile-container">
        <div className="complete-profile-card">
          <h2 className="complete-profile-title">Completar Perfil</h2>
          <p className="complete-profile-subtitle">
            Para continuar, necesitamos que completes tu información personal
          </p>

          {/* Indicador de secciones */}
          <div className="complete-profile-sections-indicator">
            <div className={`section-indicator ${currentSection >= 1 ? 'active' : ''} ${currentSection === 1 ? 'current' : ''}`}>
              <span className="section-number">1</span>
              <span className="section-label">Datos Personales</span>
            </div>
            <div className={`section-indicator ${currentSection >= 2 ? 'active' : ''} ${currentSection === 2 ? 'current' : ''}`}>
              <span className="section-number">2</span>
              <span className="section-label">Estudio y Trabajo</span>
            </div>
            <div className={`section-indicator ${currentSection >= 3 ? 'active' : ''} ${currentSection === 3 ? 'current' : ''}`}>
              <span className="section-number">3</span>
              <span className="section-label">Información Adicional</span>
            </div>
          </div>

          <ConfirmPopup
            open={Boolean(error)}
            title="Error"
            message={error}
            confirmLabel="Aceptar"
            cancelLabel={null}
            onConfirm={() => setError('')}
            onCancel={() => setError('')}
            variant="danger"
          />

          <form onSubmit={handleSubmit} className="complete-profile-form">
            {/* Sección 1: Datos Personales */}
            {currentSection === 1 && (
              <div className="complete-profile-section">
                <h3 className="complete-profile-section-title">Datos Personales</h3>
                
                <div className="complete-profile-form-row">
                  <div className="complete-profile-form-group">
                    <label htmlFor="first_name" className="complete-profile-label">
                      Nombre/s *
                      {completedFields.first_name && <span className="field-completed-badge">✓ Completado</span>}
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className={`complete-profile-input ${completedFields.first_name ? 'field-completed' : ''}`}
                      required
                      autoComplete="given-name"
                      readOnly={completedFields.first_name}
                      disabled={completedFields.first_name}
                    />
                  </div>

                  <div className="complete-profile-form-group">
                    <label htmlFor="last_name" className="complete-profile-label">
                      Apellido/s *
                      {completedFields.last_name && <span className="field-completed-badge">✓ Completado</span>}
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className={`complete-profile-input ${completedFields.last_name ? 'field-completed' : ''}`}
                      required
                      autoComplete="family-name"
                      readOnly={completedFields.last_name}
                      disabled={completedFields.last_name}
                    />
                  </div>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="dni" className="complete-profile-label">
                    DNI *
                    {completedFields.dni && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <input
                    type="text"
                    id="dni"
                    name="dni"
                    value={formData.dni}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.dni ? 'field-completed' : ''}`}
                    required
                    pattern="[A-Za-z0-9]{7,20}"
                    maxLength="20"
                    title="Solo letras y números, entre 7 y 20 caracteres (sin espacios ni signos)"
                    autoComplete="off"
                    readOnly={completedFields.dni}
                    disabled={completedFields.dni}
                  />
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="genero_id" className="complete-profile-label">
                    Género *
                    {completedFields.genero_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="genero_id"
                    name="genero_id"
                    value={formData.genero_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.genero_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.genero_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.generos.map(genero => (
                      <option key={genero.id} value={genero.id}>
                        {genero.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="fecha_nacimiento" className="complete-profile-label">
                    Fecha de Nacimiento *
                    {completedFields.fecha_nacimiento && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <input
                    type="date"
                    id="fecha_nacimiento"
                    name="fecha_nacimiento"
                    value={formData.fecha_nacimiento}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.fecha_nacimiento ? 'field-completed' : ''}`}
                    required
                    max={new Date().toISOString().split('T')[0]}
                    readOnly={completedFields.fecha_nacimiento}
                    disabled={completedFields.fecha_nacimiento}
                  />
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="telefono_celular" className="complete-profile-label">
                    Teléfono Celular *
                    {completedFields.telefono_celular && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <input
                    type="tel"
                    id="telefono_celular"
                    name="telefono_celular"
                    value={formData.telefono_celular}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.telefono_celular ? 'field-completed' : ''}`}
                    required
                    autoComplete="tel"
                    maxLength={20}
                    pattern="\+?[0-9]+"
                    title="Solo números; opcionalmente un + al inicio"
                    readOnly={completedFields.telefono_celular}
                    disabled={completedFields.telefono_celular}
                  />
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="pais_id" className="complete-profile-label">
                    País *
                    {completedFields.pais_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="pais_id"
                    name="pais_id"
                    value={formData.pais_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.pais_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.pais_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.paises.map(pais => (
                      <option key={pais.id} value={pais.id}>
                        {pais.nombre}
                      </option>
                    ))}
                    <option value="otro">Otro</option>
                  </select>
                  {formData.pais_id === 'otro' && (
                    <input
                      type="text"
                      name="pais_otro"
                      value={formData.pais_otro}
                      onChange={handleChange}
                      placeholder="Escribí tu país"
                      className="complete-profile-input complete-profile-input--mt"
                      maxLength={150}
                      disabled={completedFields.pais_id}
                    />
                  )}
                </div>

                {(formData.pais_id || formData.pais_otro) && (
                  <div className="complete-profile-form-group">
                    <label htmlFor="provincia_id" className="complete-profile-label">
                      Provincia *
                      {completedFields.provincia_id && <span className="field-completed-badge">✓ Completado</span>}
                    </label>
                    <select
                      id="provincia_id"
                      name="provincia_id"
                      value={formData.provincia_id}
                      onChange={handleChange}
                      className={`complete-profile-input ${completedFields.provincia_id ? 'field-completed' : ''}`}
                      required
                      disabled={completedFields.provincia_id}
                    >
                      <option value="">Seleccionar...</option>
                      {options.provincias.map(provincia => (
                        <option key={provincia.id} value={provincia.id}>
                          {provincia.nombre}
                        </option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.provincia_id === 'otro' && (
                      <input
                        type="text"
                        name="provincia_otro"
                        value={formData.provincia_otro}
                        onChange={handleChange}
                        placeholder="Escribí tu provincia"
                        className="complete-profile-input complete-profile-input--mt"
                        maxLength={150}
                        disabled={completedFields.provincia_id}
                      />
                    )}
                  </div>
                )}

                {(formData.provincia_id || formData.provincia_otro) && (
                  <div className="complete-profile-form-group">
                    <label htmlFor="localidad_id" className="complete-profile-label">
                      Partido *
                      {completedFields.localidad_id && <span className="field-completed-badge">✓ Completado</span>}
                    </label>
                    <select
                      id="localidad_id"
                      name="localidad_id"
                      value={formData.localidad_id}
                      onChange={handleChange}
                      className={`complete-profile-input ${completedFields.localidad_id ? 'field-completed' : ''}`}
                      required
                      disabled={completedFields.localidad_id}
                    >
                      <option value="">Seleccionar...</option>
                      {options.localidades.map(localidad => (
                        <option key={localidad.id} value={localidad.id}>
                          {localidad.nombre}
                        </option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.localidad_id === 'otro' && (
                      <input
                        type="text"
                        name="localidad_otro"
                        value={formData.localidad_otro}
                        onChange={handleChange}
                        placeholder="Escribí tu partido"
                        className="complete-profile-input complete-profile-input--mt"
                        maxLength={150}
                        disabled={completedFields.localidad_id}
                      />
                    )}
                  </div>
                )}

                {(formData.localidad_id || formData.localidad_otro) && (
                  <div className="complete-profile-form-group">
                    <label htmlFor="barrio_id" className="complete-profile-label">
                      Barrio *
                      {completedFields.barrio_id && <span className="field-completed-badge">✓ Completado</span>}
                    </label>
                    <select
                      id="barrio_id"
                      name="barrio_id"
                      value={formData.barrio_id}
                      onChange={handleChange}
                      className={`complete-profile-input ${completedFields.barrio_id ? 'field-completed' : ''}`}
                      required
                      disabled={completedFields.barrio_id}
                    >
                      <option value="">Seleccionar...</option>
                      {options.barrios.map(barrio => (
                        <option key={barrio.id} value={barrio.id}>
                          {barrio.nombre}
                        </option>
                      ))}
                      <option value="otro">Otro</option>
                    </select>
                    {formData.barrio_id === 'otro' && (
                      <input
                        type="text"
                        name="barrio_otro"
                        value={formData.barrio_otro}
                        onChange={handleChange}
                        placeholder="Escribí tu barrio"
                        className="complete-profile-input complete-profile-input--mt"
                        maxLength={150}
                        disabled={completedFields.barrio_id}
                      />
                    )}
                  </div>
                )}

                <div className="complete-profile-section-buttons">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="complete-profile-button complete-profile-button-next"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}

            {/* Sección 2: Estudio y Trabajo */}
            {currentSection === 2 && (
              <div className="complete-profile-section">
                <h3 className="complete-profile-section-title">Estudio y Trabajo</h3>

                <div className="complete-profile-form-group">
                  <label htmlFor="tipo_educacion_secundaria_id" className="complete-profile-label">
                    ¿Dónde estudias o estudiaste la escuela Secundaria? *
                    {completedFields.tipo_educacion_secundaria_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="tipo_educacion_secundaria_id"
                    name="tipo_educacion_secundaria_id"
                    value={formData.tipo_educacion_secundaria_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.tipo_educacion_secundaria_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.tipo_educacion_secundaria_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.tiposEducacionSecundaria.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="nivel_estudio_id" className="complete-profile-label">
                    Máximo nivel de estudio alcanzado *
                    {completedFields.nivel_estudio_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="nivel_estudio_id"
                    name="nivel_estudio_id"
                    value={formData.nivel_estudio_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.nivel_estudio_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.nivel_estudio_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.nivelesEstudio.map(nivel => (
                      <option key={nivel.id} value={nivel.id}>
                        {nivel.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="situacion_laboral_id" className="complete-profile-label">
                    ¿Cuál es tu situación laboral actual? *
                    {completedFields.situacion_laboral_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="situacion_laboral_id"
                    name="situacion_laboral_id"
                    value={formData.situacion_laboral_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.situacion_laboral_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.situacion_laboral_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.situacionesLaborales.map(situacion => (
                      <option key={situacion.id} value={situacion.id}>
                        {situacion.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedSituacionLaboral?.requiere_area && (
                  <div className="complete-profile-form-group">
                    <label htmlFor="area_trabajo_id" className="complete-profile-label">
                      En caso de contar con empleo, ¿en qué área o industria te desempeñás actualmente? *
                      {completedFields.area_trabajo_id && <span className="field-completed-badge">✓ Completado</span>}
                    </label>
                    <select
                      id="area_trabajo_id"
                      name="area_trabajo_id"
                      value={formData.area_trabajo_id}
                      onChange={handleChange}
                      className={`complete-profile-input ${completedFields.area_trabajo_id ? 'field-completed' : ''}`}
                      required
                      disabled={completedFields.area_trabajo_id}
                    >
                      <option value="">Seleccionar...</option>
                      {options.areasTrabajo.map(area => (
                        <option key={area.id} value={area.id}>
                          {area.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="complete-profile-form-group">
                  <label className="complete-profile-label">
                    ¿En qué campos contás con conocimientos o habilidades? (puedes seleccionar múltiples)
                    {completedFields.habilidades_ids && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <div className="complete-profile-checkboxes">
                    {options.habilidades.map(habilidad => (
                      <label key={habilidad.id} className={`complete-profile-checkbox-label ${completedFields.habilidades_ids ? 'complete-profile-checkbox-label--disabled' : ''}`}>
                        <input
                          type="checkbox"
                          name="habilidades_ids"
                          value={habilidad.id}
                          checked={
                            habilidad.id === otrosId
                              ? (formData.habilidades_ids.includes(otrosId) || otrasHabilidades.some(t => t && t.trim()))
                              : formData.habilidades_ids.includes(habilidad.id)
                          }
                          onChange={handleChange}
                          className="complete-profile-checkbox"
                          disabled={completedFields.habilidades_ids}
                        />
                        <span>{habilidad.nombre}</span>
                      </label>
                    ))}
                  </div>
                  {(formData.habilidades_ids.includes(otrosId) || otrasHabilidades.some(t => t && t.trim())) && (
                    <div className="complete-profile-otras-habilidades">
                      <span className="complete-profile-label complete-profile-otras-label">
                        Otras habilidades (una por línea):
                      </span>
                      {otrasHabilidades.map((valor, idx) => (
                        <div key={idx} className="complete-profile-otras-row">
                          <input
                            type="text"
                            value={valor}
                            onChange={(e) => {
                              const next = [...otrasHabilidades];
                              next[idx] = e.target.value;
                              setOtrasHabilidades(next);
                            }}
                            className="complete-profile-input complete-profile-otras-input"
                            placeholder="Ej: Diseño gráfico"
                            disabled={completedFields.habilidades_ids}
                          />
                          {idx === otrasHabilidades.length - 1 ? (
                            <button
                              type="button"
                              className="complete-profile-button complete-profile-button-add-otras"
                              onClick={() => setOtrasHabilidades(prev => [...prev, ''])}
                              title="Agregar otra habilidad"
                              aria-label="Agregar otra habilidad"
                            >
                              +
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="complete-profile-button complete-profile-button-remove-otras"
                              onClick={() => setOtrasHabilidades(prev => prev.filter((_, i) => i !== idx))}
                              title="Quitar"
                              aria-label="Quitar"
                            >
                              −
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="complete-profile-section-buttons">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="complete-profile-button complete-profile-button-secondary"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="complete-profile-button complete-profile-button-next"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}

            {/* Sección 3: Información Adicional */}
            {currentSection === 3 && (
              <div className="complete-profile-section">
                <h3 className="complete-profile-section-title">Información Adicional</h3>

                <div className="complete-profile-form-group">
                  <label htmlFor="objetivo_curso_id" className="complete-profile-label">
                    ¿Cuál es tu principal objetivo de inscribirte a un curso? *
                    {completedFields.objetivo_curso_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="objetivo_curso_id"
                    name="objetivo_curso_id"
                    value={formData.objetivo_curso_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.objetivo_curso_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.objetivo_curso_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.objetivosCurso.map(objetivo => (
                      <option key={objetivo.id} value={objetivo.id}>
                        {objetivo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="horas_dedicacion_id" className="complete-profile-label">
                    ¿Cuántas horas por semana podes dedicarle al curso aproximadamente? *
                    {completedFields.horas_dedicacion_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="horas_dedicacion_id"
                    name="horas_dedicacion_id"
                    value={formData.horas_dedicacion_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.horas_dedicacion_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.horas_dedicacion_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.horasDedicacion.map(horas => (
                      <option key={horas.id} value={horas.id}>
                        {horas.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="tiene_computadora_internet" className="complete-profile-label">
                    ¿Contas con una computadora y/o acceso a internet? *
                    {completedFields.tiene_computadora_internet && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="tiene_computadora_internet"
                    name="tiene_computadora_internet"
                    value={formData.tiene_computadora_internet}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.tiene_computadora_internet ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.tiene_computadora_internet}
                  >
                    <option value="">Seleccionar...</option>
                    <option value="true">Sí</option>
                    <option value="false">No</option>
                  </select>
                </div>

                <div className="complete-profile-form-group">
                  <label htmlFor="fuente_informacion_id" className="complete-profile-label">
                    ¿Cómo te enteraste de Tecno 3F? *
                    {completedFields.fuente_informacion_id && <span className="field-completed-badge">✓ Completado</span>}
                  </label>
                  <select
                    id="fuente_informacion_id"
                    name="fuente_informacion_id"
                    value={formData.fuente_informacion_id}
                    onChange={handleChange}
                    className={`complete-profile-input ${completedFields.fuente_informacion_id ? 'field-completed' : ''}`}
                    required
                    disabled={completedFields.fuente_informacion_id}
                  >
                    <option value="">Seleccionar...</option>
                    {options.fuentesInformacion.map(fuente => (
                      <option key={fuente.id} value={fuente.id}>
                        {fuente.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="complete-profile-section-buttons">
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="complete-profile-button complete-profile-button-secondary"
                  >
                    Anterior
                  </button>
                  <button
                    type="submit"
                    className="complete-profile-button complete-profile-button-primary"
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Completar Perfil'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
